import { useState } from 'react'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  redirect,
  useMatches,
  useRouteError,
} from 'react-router'
import { HydrationBoundary, QueryClient, QueryClientProvider, type DehydratedState } from '@tanstack/react-query'
import type { Route } from './+types/root'
// Side-effect import (not ?url + links): React Router then emits the one
// client-build CSS file on both server and client. A ?url import is hashed
// separately per build environment by Tailwind, so the server linked a CSS
// file the client didn't expect and every page failed hydration (#418).
import './styles/global.css'
import { getSiteUrl, isCanonicalHost } from './lib/site.server'
import { DEFAULT_LOCALE, messages, type Locale } from './lib/i18n/messages'
import { BASE_TITLE } from './lib/seo'

export async function loader({ request, url }: Route.LoaderArgs) {
  // `url` (normalised: no `.data` suffix on client navigations), never
  // `new URL(request.url)` — see CLAUDE.md, "v8_passThroughRequests".
  // One URL per page: `/about/` → `/about` (old WordPress links all had slashes).
  // Collapse leading slashes too: a Location of `//evil.com` would be read as
  // a protocol-relative URL — an open redirect off the site.
  if (url.pathname.length > 1 && url.pathname.endsWith('/')) {
    const clean = `/${url.pathname.replace(/^\/+|\/+$/g, '')}`
    throw redirect(`${clean}${url.search}`, 301)
  }
  const siteUrl = getSiteUrl(request)
  return { siteUrl, indexable: isCanonicalHost(request, siteUrl) }
}

export const links: Route.LinksFunction = () => [
  // Replace public/favicon.svg with the client's icon (add PNG sizes if needed).
  { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
  { rel: 'apple-touch-icon', href: '/icons/apple-touch-icon.png' },
]

const RYBBIT_SITE_ID = import.meta.env.VITE_RYBBIT_SITE_ID as string | undefined

/** Locale of the matched locale layout (drives `<html lang>`). */
function useMatchedLocale(): Locale {
  const matches = useMatches()
  for (const m of matches) {
    const locale = (m.loaderData as { locale?: Locale } | undefined)?.locale
    if (locale) return locale
  }
  return DEFAULT_LOCALE
}

export function Layout({ children }: { children: React.ReactNode }) {
  const locale = useMatchedLocale()
  return (
    <html lang={locale}>
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#981E32" />
        <Meta />
        <Links />
        {RYBBIT_SITE_ID ? (
          <script defer src="https://app.rybbit.io/api/script.js" data-site-id={RYBBIT_SITE_ID} />
        ) : null}
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

/**
 * Every route loader may return a `dehydratedState` of prefetched CMS content;
 * merge them all into the one browser QueryClient so components render with
 * data on the first (server and hydration) render.
 */
function useMergedDehydratedState(): DehydratedState {
  const matches = useMatches()
  const queries = matches.flatMap(
    (m) => (m.loaderData as { dehydratedState?: DehydratedState } | undefined)?.dehydratedState?.queries ?? [],
  )
  return { mutations: [], queries }
}

export default function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        // Server-fetched content counts as fresh for a while, so hydration does
        // not immediately refetch everything; a reload always gets fresh HTML.
        defaultOptions: { queries: { staleTime: 5 * 60 * 1000 } },
      }),
  )
  const dehydratedState = useMergedDehydratedState()
  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <Outlet />
      </HydrationBoundary>
    </QueryClientProvider>
  )
}

/** Unexpected errors (e.g. the CMS is down on a detail page). 404s are
 *  rendered by the pages themselves with a 404 status, not thrown. */
export function ErrorBoundary() {
  const error = useRouteError()
  const status = isRouteErrorResponse(error) ? error.status : 500
  const t = messages[useMatchedLocale()]
  return (
    <main style={{ padding: 'clamp(48px,8vw,96px) 24px', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
      <title>{`${t.errorPage.title(status)} — ${BASE_TITLE}`}</title>
      <meta name="robots" content="noindex" />
      <h1>{t.errorPage.heading}</h1>
      <p>{t.errorPage.body}</p>
      <p><a href="/">{t.common.toFrontpage}</a></p>
    </main>
  )
}
